/**
 * Data Type Parser and Optimistic Conversion Utilities
 * Handles schema data type mismatches with graceful degradation
 * Part of T010 - Schema-Driven Table Component implementation
 */

import { logWarning } from './error-handler'

/**
 * Safely parses a value based on the expected schema data type
 * Uses optimistic conversion with fallback to string representation
 * @param {any} value - The raw value to parse
 * @param {string} expectedType - The expected data type from schema
 * @param {string} columnKey - Column identifier for debugging
 * @returns {any} The parsed value or fallback representation
 */
export function parseDataType(value, expectedType, columnKey = 'unknown') {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return getDefaultForType(expectedType)
  }

  try {
    switch (expectedType && expectedType.toLowerCase()) {
      case 'string':
        return parseString(value)
      
      case 'number':
      case 'integer':
      case 'float':
        return parseNumber(value, columnKey)
      
      case 'boolean':
      case 'bool':
        return parseBoolean(value, columnKey)
      
      case 'date':
      case 'datetime':
        return parseDate(value, columnKey)
      
      case 'currency':
        return parseCurrency(value, columnKey)
      
      case 'percentage':
      case 'percent':
        return parsePercentage(value, columnKey)
      
      case 'array':
        return parseArray(value, columnKey)
      
      case 'object':
        return parseObject(value, columnKey)
      
      default:
        // Unknown type - return as string
        logWarning(`Unknown data type "${expectedType}" for column ${columnKey}, defaulting to string`)
        return parseString(value)
    }
  } catch (error) {
    logWarning(`Data parsing failed for column ${columnKey}: ${error.message}`)
    return parseString(value) // Final fallback
  }
}

/**
 * Parse value as string (most permissive)
 */
function parseString(value) {
  if (typeof value === 'string') return value
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Parse value as number with optimistic conversion
 */
function parseNumber(value, columnKey) {
  if (typeof value === 'number') return value
  
  if (typeof value === 'string') {
    // Handle common currency formats (£1,234.56, $1,234.56)
    const cleanValue = value.replace(/[£$€,\s]/g, '')
    const parsed = Number(cleanValue)
    
    if (!isNaN(parsed)) return parsed
    
    // Try parseInt for strings like "123abc"
    const intParsed = parseInt(cleanValue, 10)
    if (!isNaN(intParsed)) {
      logWarning(`Column ${columnKey}: Non-numeric characters found, extracted number ${intParsed}`)
      return intParsed
    }
  }
  
  if (typeof value === 'boolean') return value ? 1 : 0
  
  logWarning(`Column ${columnKey}: Cannot convert "${value}" to number, returning 0`)
  return 0
}

/**
 * Parse value as boolean with optimistic conversion
 */
function parseBoolean(value, columnKey) {
  if (typeof value === 'boolean') return value
  
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim()
    
    // Explicit true values
    if (['true', 'yes', 'y', '1', 'on', 'active', 'enabled'].includes(lower)) {
      return true
    }
    
    // Explicit false values
    if (['false', 'no', 'n', '0', 'off', 'inactive', 'disabled'].includes(lower)) {
      return false
    }
    
    // Non-empty string is truthy
    if (value.length > 0) {
      logWarning(`Column ${columnKey}: Ambiguous boolean value "${value}", treating as true`)
      return true
    }
    
    return false
  }
  
  if (typeof value === 'number') return value !== 0
  
  // Use JavaScript's truthiness
  return Boolean(value)
}

/**
 * Parse value as date with optimistic conversion
 */
function parseDate(value, columnKey) {
  if (value instanceof Date) return value
  
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    
    if (!isNaN(parsed.getTime())) return parsed
    
    // Try common date formats
    const commonFormats = [
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
      /^\d{2}[\/\-\.]\d{2}[\/\-\.]\d{4}$/, // DD/MM/YYYY or DD-MM-YYYY
      /^\d{2}[\/\-\.]\d{2}[\/\-\.]\d{2}$/ // DD/MM/YY or DD-MM-YY
    ]
    
    for (const format of commonFormats) {
      if (format.test(value)) {
        const attemptParsed = new Date(value)
        if (!isNaN(attemptParsed.getTime())) {
          return attemptParsed
        }
      }
    }
  }
  
  logWarning(`Column ${columnKey}: Cannot parse "${value}" as date, returning current date`)
  return new Date()
}

/**
 * Parse value as currency (returns number)
 */
function parseCurrency(value, columnKey) {
  if (typeof value === 'number') return value
  
  if (typeof value === 'string') {
    // Remove currency symbols and thousands separators
    const cleanValue = value.replace(/[£$€¥₹₽¢¤,\s]/g, '')
    const parsed = parseFloat(cleanValue)
    
    if (!isNaN(parsed)) return parsed
  }
  
  // Fallback to number parsing
  return parseNumber(value, columnKey)
}

/**
 * Parse value as percentage (returns number between 0 and 100)
 */
function parsePercentage(value, columnKey) {
  if (typeof value === 'number') {
    // Assume values > 1 are already percentages, values <= 1 are decimals
    return value > 1 ? value : value * 100
  }
  
  if (typeof value === 'string') {
    const cleanValue = value.replace(/[%\s]/g, '')
    const parsed = parseFloat(cleanValue)
    
    if (!isNaN(parsed)) return parsed
  }
  
  logWarning(`Column ${columnKey}: Cannot parse "${value}" as percentage, returning 0`)
  return 0
}

/**
 * Parse value as array
 */
function parseArray(value, columnKey) {
  if (Array.isArray(value)) return value
  
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // Try comma-separated values
      return value.split(',').map(item => item.trim())
    }
  }
  
  // Single value becomes array
  return [value]
}

/**
 * Parse value as object
 */
function parseObject(value, columnKey) {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value
  }
  
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch {
      logWarning(`Column ${columnKey}: Cannot parse "${value}" as JSON object`)
    }
  }
  
  // Fallback: wrap primitive in object
  return { value }
}

/**
 * Get default value for a given type
 */
function getDefaultForType(type) {
  switch (type && type.toLowerCase()) {
    case 'string': return ''
    case 'number':
    case 'integer':
    case 'float':
    case 'currency':
    case 'percentage': return 0
    case 'boolean':
    case 'bool': return false
    case 'date':
    case 'datetime': return new Date()
    case 'array': return []
    case 'object': return {}
    default: return null
  }
}

/**
 * Validate if a value matches the expected type
 * @param {any} value - The value to validate
 * @param {string} expectedType - The expected data type
 * @returns {boolean} True if value matches type
 */
export function validateDataType(value, expectedType) {
  if (value === null || value === undefined) return true // Nullable by default
  
  switch (expectedType && expectedType.toLowerCase()) {
    case 'string':
      return typeof value === 'string'
    
    case 'number':
    case 'integer':
    case 'float':
    case 'currency':
    case 'percentage':
      return typeof value === 'number' && !isNaN(value)
    
    case 'boolean':
    case 'bool':
      return typeof value === 'boolean'
    
    case 'date':
    case 'datetime':
      return value instanceof Date && !isNaN(value.getTime())
    
    case 'array':
      return Array.isArray(value)
    
    case 'object':
      return typeof value === 'object' && !Array.isArray(value)
    
    default:
      return true // Unknown types are valid
  }
}

/**
 * Get type information for debugging
 * @param {any} value - The value to analyze
 * @returns {object} Type analysis information
 */
export function getTypeAnalysis(value) {
  return {
    actualType: Array.isArray(value) ? 'array' : typeof value,
    isNull: value === null,
    isUndefined: value === undefined,
    constructor: (value && value.constructor && value.constructor.name) || 'none',
    stringRepresentation: String(value),
    canBeNumber: !isNaN(Number(value)),
    canBeDate: !isNaN(new Date(value).getTime())
  }
}